import type { InnointelCompany } from "components/innointel/types";

type CompanyLocations = NonNullable<InnointelCompany["location"]>;
type Coordinates = { latitude?: number; longitude?: number };
type Address = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postcode?: string;
  basic?: string | null;
};

export type CompanyPlace = {
  key: string;
  label: string;
  address?: Address;
  coordinates?: Coordinates;
};

const EARTH_RADIUS_KM = 6371;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

export const distanceKm = (from?: Coordinates, to?: Coordinates) => {
  if (
    from?.latitude === undefined ||
    from.longitude === undefined ||
    to?.latitude === undefined ||
    to.longitude === undefined
  ) {
    return undefined;
  }
  const dLat = toRadians(to.latitude - from.latitude);
  const dLng = toRadians(to.longitude - from.longitude);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.latitude)) *
      Math.cos(toRadians(to.latitude)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
};

export const formatDistance = (km?: number) => {
  if (km === undefined) {
    return undefined;
  }
  return km < 10 ? `${km.toFixed(1)} km away` : `${Math.round(km)} km away`;
};

export const POSTCODE_ONLY_EXPLANATION =
  "We only know this company's postcode, not its street address, so it is shown at the middle of the postcode area.";

export const isPostcodeOnly = (address?: Address) =>
  !!address?.postcode && !address.addressLine1 && !address.basic;

/** The address in reading order, without the country, which local searches don't need. */
export const addressLines = (address?: Address) =>
  [
    address?.addressLine1 ?? address?.basic,
    address?.addressLine2,
    [address?.city, address?.postcode].filter(Boolean).join(" "),
  ].filter((line): line is string => !!line);

export const sameAddress = (a?: Address, b?: Address) => {
  const key = (address?: Address) =>
    (address?.postcode ?? address?.addressLine1 ?? address?.basic ?? "")
      .replace(/\s+/g, "")
      .toLowerCase();
  return !!key(a) && key(a) === key(b);
};

/** Every place a company is known at: where the search found it, its registered office and other offices. Places at
 * the same address as one listed before them are left out. */
export const companyPlaces = (location?: CompanyLocations): CompanyPlace[] => {
  const places: CompanyPlace[] = [];
  if (location?.search?.address || location?.search?.coordinates) {
    places.push({
      key: "search",
      label: "Found by the search",
      address: location.search.address,
      coordinates: location.search.coordinates,
    });
  }
  if (location?.registered?.address) {
    places.push({
      key: "registered",
      label: "Registered office",
      address: location.registered.address,
      coordinates: location.registered.coordinates,
    });
  }
  (location?.offices ?? []).forEach((office, index) => {
    places.push({
      key: `office-${index}`,
      label: office.name ?? "Office",
      address: office.address,
      coordinates: office.coordinates ?? undefined,
    });
  });
  return places.filter(
    (place, index) =>
      !places
        .slice(0, index)
        .some((earlier) => sameAddress(earlier.address, place.address)),
  );
};
